// #region IMPORTS
import { useContext, useEffect, useMemo, useState } from "react";

//MRT Imports
import {
	MaterialReactTable,
	useMaterialReactTable,
	type MRT_ColumnDef,
	MRT_GlobalFilterTextField,
	MRT_ToggleFiltersButton,
	MRT_RowSelectionState,
	MRT_VisibilityState,
	MRT_ColumnOrderState,
	MRT_GroupingState,
	MRT_SortingState,
	MRT_ExpandAllButton,
	MRT_ExpandButton,
	MRT_ColumnFiltersState,
	MRT_ColumnFilterFnsState,
	MRT_ShowHideColumnsButton,
	MRT_ToggleDensePaddingButton,
	MRT_ToggleFullScreenButton,
	MRT_FilterFn,
	MRT_Row,
	MRT_Column,
} from "material-react-table";

//Material UI Imports
import {
	Autocomplete,
	Box,
	Button,
	IconButton,
	Menu,
	MenuItem,
	Stack,
	TextField,
	Tooltip,
	lighten,
} from "@mui/material";

//Icons Imports
import {
	Delete,
	ContentCopy,
	Save,
	KeyboardDoubleArrowRight,
	RefreshOutlined,
	Download,
	Map,
	Check,
	MoreHoriz,
	FileDownload,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";

import { ViewDefinition, ViewGroup } from "../../utilities/types";
import { UserContext } from "../../hooks/UserContext";
import ViewSaveDialog from "./ViewSaveDialog";
import CustomAutocomplete from "../helpers/CustomAutocomplete";
import { toast } from "react-toastify";
//import useUrlState from '@ahooksjs/use-url-state';
import { MarkJobReadyPopover } from "./MarkJobReadyPopover";
import { LoadingComponent } from "../helpers/LoadingComponent";
import PortalTooltip from "../helpers/PortalTooltip";
import { Props as ViewSaveDialogProps } from "./ViewSaveDialog";

import * as Sentry from "@sentry/react";
import React from "react";
import { API } from "../../api";
import type { Branch, Company, Job, Contact, Region } from "../../api/types";
import { INVALID_USERCONTEXT } from "../../utilities/constants";
import { Oops } from "../../utilities/oops";
import { checkboxMultiselectColumn } from "../helpers/MRT/MultiSelectCheckboxColumn";
import { textColumn } from "../helpers/MRT/TextColumn";
import { multiSelectColumn } from "../helpers/MRT/MultiSelectTextColumn";
import { numericColumn } from "../helpers/MRT/NumericColumn";
import { dateColumn } from "../helpers/MRT/DateColumn";
import { checkboxColumn } from "../helpers/MRT/CheckboxColumn";
import { GeoJSON } from "../../utilities/utils";
import { IAirtableAttachment } from "@rogoag/airtable";
import { FileDownloader } from "../../utilities/downloadFile";
import { Row } from "@tanstack/table-core";
import { Filter } from "../../api/common";
// #endregion

// #region FILTERS / VIEWS
const DEFAULT_FILTER_FNS: MRT_ColumnFilterFnsState = {
	grower: "fuzzy",
	field: "fuzzy",
	farm: "fuzzy",
	"job status": "arrIncludesSome",
	"boundary acres": "between",
	branch: "arrIncludesSome",
	region: "arrIncludesSome",
	"field ready date": "between",
	"sample date": "between",
	"creation date": "between",
	"drop/ship date": "between",
	"test package": "arrIncludesSome",
	"boundary change?": "equals",
	"boundary approved?": "equals",
	"points change?": "equals",
	submitter: "arrIncludesSome",
	season: "arrIncludesSome",
	"approved billing?": "equals",
	"job flags": "arrIncludesSome",
	"lab sent results": "between",
	"event id": "fuzzy",
	"lab submittal id": "fuzzy",
	depth: "between",
	"sample changes": "fuzzy",
	"lab name": "fuzzy",
	"test job?": "equals",
} as const;

const isArrayBasedFilter = (filterFn: string): boolean => {
	return filterFn === "arrIncludesSome" || filterFn === "arrIncludes";
};

const DEFAULT_FILTERS: MRT_ColumnFiltersState = [];
const DEFAULT_GROUPING: MRT_GroupingState = ["grower"];
const DEFAULT_COLUMN_SORT: MRT_SortingState = [];
const DEFAULT_VIS_STATE: MRT_VisibilityState = {};
const DEFAULT_COLUMN_ORDER: MRT_ColumnOrderState = [
	"mrt-row-select",
	"mrt-row-expand",
	"grower",
	"field",
	"farm",
	"job status",
	"boundary acres",
	"field ready date",
	"sample date",
	"creation date",
	"drop/ship date",
	"test package",
	"edits",
	"files last edited?",
	"submitter",
	"approved billing?",
	"lab sent results",
	"branch",
	"region",
	"job flags",
];

const PARAM_SEPARATOR = "|";

const dashboardStateToURLParams = (state: DashboardURLState): URLSearchParams => {
	const params = new URLSearchParams();
	const { columnVisibility, sorting, grouping, columnOrder, columnFilterFunctions, columnFilterValues } = state;
	const urlSorting = sorting.map((sort) => `${sort.id}:${sort.desc ? "desc" : "asc"}`).join(PARAM_SEPARATOR);
	const urlGrouping = grouping.join(PARAM_SEPARATOR);
	const urlColumnOrder = columnOrder.join(PARAM_SEPARATOR);
	const urlColumnVisibility = Object.keys(columnVisibility)
		.map((key) => `${key}=${columnVisibility[key]}`)
		.join(PARAM_SEPARATOR);
	const urlFunctions = Object.keys(columnFilterFunctions)
		.map((key) => `${key}=${columnFilterFunctions[key]}`)
		.join(PARAM_SEPARATOR);
	const urlFilterValues = columnFilterValues
		.filter((filter) => filter.value !== undefined && filter.value !== "")
		.map((filter) => {
			const value = Array.isArray(filter.value) ? filter.value.join(",") : filter.value;
			return `${filter.id}:${value}`;
		})
		.join(PARAM_SEPARATOR);

	params.set("sort", urlSorting);
	params.set("group", urlGrouping);
	params.set("vis", urlColumnVisibility);
	params.set("order", urlColumnOrder);
	params.set("fns", urlFunctions);
	params.set("filter", urlFilterValues);

	params.sort();
	return params;
};

const DEFAULT_VIEW_NAME = "Group by Season, Branch, Grower";

const createDefaultViews = (branchCount: number): ViewDefinition[] => {
	// Determine grouping based on branch count (same logic as normalizeUrlParamsForComparison)
	const baseGrouping = ["season", "branch", "grower"];
	const grouping = branchCount <= 1 ? baseGrouping.filter((group) => group !== "branch") : baseGrouping;

	return [
		{
			name: DEFAULT_VIEW_NAME,
			group: ViewGroup.Default,
			url: dashboardStateToURLParams({
				columnFilterFunctions: DEFAULT_FILTER_FNS,
				columnFilterValues: DEFAULT_FILTERS,
				columnOrder: DEFAULT_COLUMN_ORDER,
				columnVisibility: {
					grower: false,
					branch: false,
					region: false,
					season: false,
					"test job?": false,
				},
				grouping: grouping,
				sorting: DEFAULT_COLUMN_SORT,
			}).toString(),
		},
		{
			name: "Group by Status",
			group: ViewGroup.Default,
			url: dashboardStateToURLParams({
				columnFilterFunctions: DEFAULT_FILTER_FNS,
				columnFilterValues: DEFAULT_FILTERS,
				columnOrder: DEFAULT_COLUMN_ORDER,
				columnVisibility: {
					"job status": false,
					"test job?": false,
				},
				grouping: ["job status"],
				sorting: DEFAULT_COLUMN_SORT,
			}).toString(),
		},
		{
			name: "Unready Jobs",
			group: ViewGroup.Default,
			url: dashboardStateToURLParams({
				columnFilterFunctions: DEFAULT_FILTER_FNS,
				columnFilterValues: [{ id: "job status", value: "0. Unready" }],
				columnOrder: DEFAULT_COLUMN_ORDER,
				columnVisibility: {
					grower: true,
					field: true,
					farm: true,
					"job status": true,
					"test package": false,
					"field ready date": false,
					"sample date": false,
					"creation date": false,
					"drop/ship date": false,
					edits: false,
					"files last edited?": false,
					submitter: false,
					"approved billing?": false,
					boundary: false,
					"lab sent results": false,
					branch: false,
					region: false,
					"job flags": false,
					"points change?": true,
					"boundary change?": true,
					"boundary approved?": true,
					"test job?": false,
				},
				grouping: ["grower"],
				sorting: DEFAULT_COLUMN_SORT,
			}).toString(),
		},
		{
			name: "Boundary Changes",
			group: ViewGroup.Default,
			url: dashboardStateToURLParams({
				columnFilterFunctions: DEFAULT_FILTER_FNS,
				columnFilterValues: [
					{ id: "boundary change?", value: true },
					{ id: "boundary approved?", value: true },
				],
				columnOrder: DEFAULT_COLUMN_ORDER,
				columnVisibility: {
					grower: true,
					field: true,
					farm: true,
					"job status": true,
					"boundary acres": false,
					"field ready date": false,
					"sample date": false,
					"creation date": false,
					"drop/ship date": false,
					"test package": false,
					edits: false,
					"files last edited?": false,
					submitter: false,
					"approved billing?": false,
					boundary: false,
					"lab sent results": false,
					branch: false,
					region: false,
					"job flags": false,
					"points change?": true,
					"boundary change?": true,
					"boundary approved?": true,
					"test job?": false,
				},
				grouping: ["grower"],
				sorting: DEFAULT_COLUMN_SORT,
			}).toString(),
		},
		{
			name: "Points Changes",
			group: ViewGroup.Default,
			url: dashboardStateToURLParams({
				columnFilterFunctions: DEFAULT_FILTER_FNS,
				columnFilterValues: [{ id: "points change?", value: true }],
				columnOrder: DEFAULT_COLUMN_ORDER,
				columnVisibility: {
					grower: true,
					field: true,
					farm: true,
					"job status": true,
					"boundary acres": false,
					"field ready date": false,
					"sample date": false,
					"creation date": false,
					"drop/ship date": false,
					"test package": false,
					edits: false,
					"files last edited?": false,
					submitter: false,
					"approved billing?": false,
					boundary: false,
					"lab sent results": false,
					branch: false,
					region: false,
					"job flags": false,
					"points change?": true,
					"boundary change?": true,
					"boundary approved?": true,
					"test job?": false,
				},
				grouping: ["grower"],
				sorting: DEFAULT_COLUMN_SORT,
			}).toString(),
		},
	];
};

// Legacy static default views for backward compatibility and initial state
const DEFAULT_VIEWS: ViewDefinition[] = createDefaultViews(2); // Assume multiple branches for initial state

interface DashboardURLState {
	sorting: MRT_SortingState;
	grouping: MRT_GroupingState;
	columnOrder: MRT_ColumnOrderState;
	columnVisibility: MRT_VisibilityState;
	columnFilterFunctions: MRT_ColumnFilterFnsState;
	columnFilterValues: MRT_ColumnFiltersState;
}

const urlParamsToDashboardState = (urlParams: URLSearchParams): DashboardURLState => {
	const rawSortParam = urlParams.get("sort");
	const rawGroupParam = urlParams.get("group");
	const rawOrderParam = urlParams.get("order");
	const rawVisParam = urlParams.get("vis");
	const rawFnsParam = urlParams.get("fns");
	const rawFilterParam = urlParams.get("filter");
	const columnFilterValues = rawFilterParam
		? rawFilterParam
				.split(PARAM_SEPARATOR)
				.map((filter) => {
					const [id, rawValue] = filter.split(":");
					if (!rawValue || rawValue === ",") return null;

					// Check if this column uses an array-based filter
					const filterFn = DEFAULT_FILTER_FNS[id as keyof typeof DEFAULT_FILTER_FNS];
					const value = filterFn && isArrayBasedFilter(filterFn) ? rawValue.split(",").map((v) => v.trim()) : rawValue;

					return { id, value };
				})
				.filter((filter): filter is { id: string; value: any } => filter !== null)
		: [];
	const viewState: DashboardURLState = {
		sorting: rawSortParam
			? rawSortParam
					.split(PARAM_SEPARATOR)
					.map((sort) => ({ id: sort.split(":")[0], desc: sort.split(":")[1] === "desc" }))
			: [],
		grouping: rawGroupParam ? rawGroupParam.split(PARAM_SEPARATOR) : [],
		columnOrder: rawOrderParam ? rawOrderParam.split(PARAM_SEPARATOR) : [],
		columnVisibility: rawVisParam
			? rawVisParam.split(PARAM_SEPARATOR).reduce((acc: MRT_VisibilityState, entry) => {
					const [key, value] = entry.split("=");
					acc[key] = value === "true";
					return acc;
				}, {})
			: {},
		columnFilterFunctions: rawFnsParam
			? rawFnsParam.split(PARAM_SEPARATOR).reduce(
					(acc: MRT_ColumnFilterFnsState, entry) => {
						const [key, value] = entry.split("=");
						acc[key] = value;
						return acc;
					},
					{ ...DEFAULT_FILTER_FNS },
				)
			: { ...DEFAULT_FILTER_FNS },
		columnFilterValues,
	};
	return viewState;
};

function normalizeUrlParamsForComparison(urlParams: URLSearchParams, branchCount: number): URLSearchParams {
	const normalizedParams = new URLSearchParams(urlParams.toString());

	// Remove company param as it's not part of view definitions
	normalizedParams.delete("company");

	// If there's only one branch, remove branch from grouping to match the runtime filtering
	if (branchCount <= 1) {
		const rawGroupParam = normalizedParams.get("group");
		if (rawGroupParam) {
			const grouping = rawGroupParam.split(PARAM_SEPARATOR);
			const filteredGrouping = grouping.filter((group) => group !== "branch");
			if (filteredGrouping.length > 0) {
				normalizedParams.set("group", filteredGrouping.join(PARAM_SEPARATOR));
			} else {
				normalizedParams.delete("group");
			}
		}
	}

	normalizedParams.sort();
	return normalizedParams;
}
// #endregion

// #region COMPONENT
interface Props {
	setDrawerOpenState: (open: boolean) => void;
	setDisplayedJobs: (jobs: Job[]) => void;
	setLoading: (loading: boolean) => void;
	drawerOpen: boolean;
}

export function JobsDashboardTable(props: Props) {
	const userContext = useContext(UserContext);

	const [inputDialogState, setInputDialogState] = useState<ViewSaveDialogProps>({
		open: false,
		title: "",
		content: "",
		submitText: "",
		submit: () => {},
		viewName: "",
	});
	// use react router search params to filter the data
	const [searchParams, setSearchParams] = useSearchParams();

	// #region STATE
	const updateSearchStateParams = () => {
		// Only update URL params if a view is selected
		if (!selectedView) {
			// During initialization, preserve existing URL params if they exist
			if (isInitializing && searchParams.toString()) {
				return;
			}
			// Clear URL params when no view is selected (after initialization)
			const urlParams = new URLSearchParams();
			if (companyOptions.length > 1 && selectedCompany) {
				urlParams.set("company", selectedCompany.id);
			}
			setSearchParams(urlParams);
			return;
		}

		const urlParams = dashboardStateToURLParams({
			sorting,
			grouping,
			columnOrder,
			columnVisibility,
			columnFilterFunctions: columnFilterFns,
			columnFilterValues: columnFilters,
		});

		// Include company parameter when multiple companies exist
		if (companyOptions.length > 1 && selectedCompany) {
			urlParams.set("company", selectedCompany.id);
		}

		urlParams.sort();
		setSearchParams(urlParams);
	};

	const dashboardState = urlParamsToDashboardState(searchParams);
	const [sorting, setSorting] = useState<MRT_SortingState>(dashboardState.sorting ?? DEFAULT_COLUMN_SORT);
	const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
		dashboardState.columnVisibility ?? DEFAULT_VIS_STATE,
	);
	const [grouping, setGrouping] = useState<MRT_GroupingState>(dashboardState.grouping ?? DEFAULT_GROUPING);
	const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>(
		dashboardState.columnOrder ?? DEFAULT_COLUMN_ORDER,
	);
	const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(DEFAULT_FILTERS);
	const [columnFilterFns, setColumnFilterFns] = useState<MRT_ColumnFilterFnsState>(
		dashboardState.columnFilterFunctions && Object.keys(dashboardState.columnFilterFunctions).length > 0
			? { ...DEFAULT_FILTER_FNS, ...dashboardState.columnFilterFunctions }
			: DEFAULT_FILTER_FNS,
	);

	const [isLoading, setIsLoading] = useState(false);
	const [updatingBoundaries] = useState(false);
	const [companyOptions, setCompanyOptions] = useState<Company[]>([]);
	const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
	const [views, setViews] = useState<ViewDefinition[]>(DEFAULT_VIEWS);
	const [selectedViewDirty, setSelectedViewDirty] = useState<boolean>(false);
	const [selectedView, setSelectedView] = useState<ViewDefinition | null>(null);
	const [branches, setBranches] = useState<Branch[]>([]);
	const [regions, setRegions] = useState<Region[]>([]);
	const [isInitializing, setIsInitializing] = useState<boolean>(true);

	const [jobs, setJobs] = useState<Job[]>([]);
	const [user, setUser] = useState<Contact>();
	const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
	const [selectionDisplayedOnMap, setSelectionDisplayedOnMap] = useState<MRT_RowSelectionState>({});

	const [markJobsReadyVisible, setMarkJobsReadyVisible] = useState(false);
	const [acresAggregation, setAcresAggregation] = useState<"total" | "average">("total");

	const [mobileMenuElement, setMobileMenuElement] = React.useState<null | HTMLElement>(null);
	const [actionMenuElement, setActionMenuElement] = React.useState<null | HTMLElement>(null);
	const [actionMenuRow, setActionMenuRow] = React.useState<MRT_Row<Job> | null>(null);

	const hasSelections = Object.keys(rowSelection).length > 0;
	const hasGrouping = grouping.length > 0;
	const tableHeight = hasSelections && hasGrouping ? `calc(100vh - 240px)` : `calc(100vh - 190px)`;

	const selectedJobs = Object.keys(rowSelection).map((key) => {
		const index = parseInt(key);
		if (!isNaN(index) && rowSelection[key]) {
			return jobs[index];
		}
	}) as Job[];

	const unreadySelectedJobs = selectedJobs?.filter((job) => !job?.dateFieldReady);

	useEffect(() => {
		// TODO Might not be necessary, might be ROGO user only setting
		const paramsCopy = new URLSearchParams(searchParams.toString());
		paramsCopy.sort();

		const selectedParams = new URLSearchParams(selectedView?.url ?? "");
		selectedParams.sort();

		setSelectedViewDirty(selectedView === null || selectedParams.toString() !== paramsCopy.toString());

		updateSearchStateParams();
	}, [selectedView, grouping, sorting, columnOrder, columnVisibility, columnFilterFns, columnFilters]);
	// #endregion

	// #region DROPDOWN LISTS
	const [jobStatusList, setJobStatusList] = useState<string[]>([]);
	const [testPackageList, setTestPackageList] = useState<string[]>([]);
	const [submitterList, setSubmitterList] = useState<string[]>([]);
	const [seasonList, setSeasonList] = useState<string[]>([]);
	const [branchList, setBranchList] = useState<string[]>([]);
	const [regionList, setRegionList] = useState<string[]>([]);
	function setMultiSelectOptions(jobs: Job[], branches: Branch[], regions: Region[]) {
		setSelectList(jobs, "status", setJobStatusList);
		setSelectList(jobs, "testPackage", setTestPackageList);
		setSelectList(jobs, "submitterName", setSubmitterList);
		setSelectList(jobs, "season", setSeasonList);
		setSelectList(jobs, "branchIds", setBranchList, branches);
		setSelectList(jobs, "regionIds", setRegionList, regions);
	}

	function setSelectList(
		jobs: Job[],
		fieldName: keyof Job,
		setList: (list: any[]) => void,
		nonJobs: (Branch | Region)[] = [],
	) {
		const all = jobs
			.map((job) => _getField(job, fieldName, nonJobs))
			.filter((value, index, self) => self.indexOf(value) === index)
			.flat();
		const distinct = [...new Set(all)].filter((value) => value !== undefined).sort();
		setList(distinct);

		function _getField(job: Job, fieldName: keyof Job, nonJobs: (Branch | Region)[] = []) {
			if (fieldName === "branchIds") {
				return getBranchField(job, nonJobs as Branch[]);
			} else if (fieldName === "regionIds") {
				return getRegionField(job, nonJobs as Region[]);
			} else {
				return job[fieldName];
			}
		}
	}

	function getFieldNameById<T extends { id: string; name: string }>(
		ids: string[] | undefined,
		mainList: T[],
		fallbackList: T[] = [],
	): string {
		if (!ids || !ids.length) {
			return "";
		}
		const id = ids[0];
		let item = mainList.find((el) => el.id === id);
		if (!item) {
			item = fallbackList.find((el) => el.id === id);
		}
		return item ? item.name : "";
	}

	function getBranchField(job: Job, b: Branch[] = []) {
		return getFieldNameById(job.branchIds, branches, b);
	}

	function getRegionField(job: Job, r: Region[] = []) {
		return getFieldNameById(job.regionIds, regions, r);
	}
	// #endregion

	// #region FUNCTIONS

	function getValuesFromFilters(filters: MRT_ColumnFiltersState, key: string): string[] {
		const filter = filters.find((filter) => filter.id === key);
		if (!filter || !filter.value) {
			return [];
		}
		return Array.isArray(filter.value) ? filter.value : [filter.value.toString()];
	}

	function getBranchesFromFilters(filters: MRT_ColumnFiltersState): string[] {
		return getValuesFromFilters(filters, "branch");
	}

	function getRegionsFromFilters(filters: MRT_ColumnFiltersState): string[] {
		return getValuesFromFilters(filters, "region");
	}

	function buildJobFilter(company: Company, branchNames: string[], regionNames: string[]): Filter<Job> {
		if (branchNames.length === 0 && regionNames.length === 0) {
			return { fields: { companyName: company.name } } as Filter<Job>;
		} else {
			const filters = [];
			if (regionNames.length > 0) {
				filters.push({
					type: "OR",
					fields: regionNames.map((name) => ({ regionIds: [name] })), // must be names instead of IDs, because of how Airtable's linked records work
				} as Filter<Job>);
			}
			if (branchNames.length > 0) {
				filters.push({
					type: "OR",
					fields: branchNames.map((name) => ({ branchIds: [name] })), // must be names instead of IDs, because of how Airtable's linked records work
				} as Filter<Job>);
			}
			return {
				type: "AND",
				fields: [
					{ companyName: company.name },
					{
						type: "OR",
						fields: filters,
					} as Filter<Job>,
				],
			} as Filter<Job>;
		}
	}

	useEffect(() => {
		// React advises to declare the async function directly inside useEffect
		async function getData() {
			try {
				setIsLoading(true);
				if (userContext == undefined || !userContext.id) {
					Sentry.captureMessage("userContext is undefined: JobsDashboardTable.tsx", "info");
					toast.error(INVALID_USERCONTEXT);
					return;
				}
				const user = await API.Data.Contacts.get.byId(userContext.id);
				const companies = await API.Data.Companies.get.byContact(user);

				setUser(user);
				companies.sort((companyA, companyB) => {
					if (companyA.name === "Rogo AG" || companyA.name === "ROGO Software") return -1;
					if (companyB.name === "Rogo AG" || companyB.name === "ROGO Software") return 1;
					return companyA.name.localeCompare(companyB.name);
				});
				setCompanyOptions(companies);
				if (companies.length === 1) {
					await onCompanyChanged(companies[0], user);
				} else if (companies.length > 1) {
					const urlCompanyId = searchParams.get("company");
					if (urlCompanyId) {
						const company = companies.find((company) => company.id === urlCompanyId);
						if (company) {
							await onCompanyChanged(company, user);
						}
					}
				}
			} catch (error) {
				Oops.handleError(error, "JobsDashboardTable.getData", userContext?.email);
			} finally {
				setIsLoading(false);
			}
		}

		if (!jobs.length) {
			getData();
		}
	}, [userContext]);

	async function onCompanyChanged(company: Company | null, userData?: Contact) {
		setIsLoading(true);
		try {
			setSelectedCompany(company);
			if (companyOptions.length > 1) {
				updateSearchStateParams();
			}
			setRowSelection({});

			const currentUser = userData || user;
			const newViews: ViewDefinition[] = currentUser?.portalSettings.views ?? [];

			let branches: Branch[] = [];
			let regions: Region[] = [];
			if (company) {
				branches = await API.Data.Branches.get.byCompany(company);
				setBranches(branches);
				regions = await API.Data.Regions.get.byCompany(company);
				setRegions(regions);
				if (company.portalSettings.views) newViews.push(...company.portalSettings.views);
			}
			newViews.push(...createDefaultViews(branches.length));

			setViews(newViews);

			// Check if URL has existing parameters that match a saved view
			if (searchParams.toString()) {
				// Find a view that matches the current URL parameters (normalized for comparison)
				const currentUrlParams = normalizeUrlParamsForComparison(searchParams, branches.length);

				const matchingView = newViews.find((view) => {
					const viewUrlParams = normalizeUrlParamsForComparison(new URLSearchParams(view.url), branches.length);
					return viewUrlParams.toString() === currentUrlParams.toString();
				});

				if (matchingView) {
					setSelectedView(matchingView);
					// Apply the dashboard state from the matching view
					const dashboardState = urlParamsToDashboardState(searchParams);
					setSorting(dashboardState.sorting);
					let grouping = dashboardState.grouping;
					// Filter out branch grouping if there is only one branch
					if (branches.length <= 1) {
						grouping = grouping.filter((group) => group !== "branch");
					}
					setGrouping(grouping);
					setColumnOrder(dashboardState.columnOrder);
					setColumnVisibility(dashboardState.columnVisibility);
					setColumnFilters(dashboardState.columnFilterValues);
					setColumnFilterFns({ ...DEFAULT_FILTER_FNS, ...dashboardState.columnFilterFunctions });

					// Load jobs only if we have a matching view
					if (company) {
						const branchNames = getBranchesFromFilters(dashboardState.columnFilterValues);
						const regionNames = getRegionsFromFilters(dashboardState.columnFilterValues);
						const filter = buildJobFilter(company, branchNames, regionNames);
						const jobs = await API.Data.Jobs.get.byFilter(filter);
						setMultiSelectOptions(jobs, branches, regions);
						setJobs(jobs);
					}
				} else {
					// URL params don't match any saved view, but still apply the state from URL
					const dashboardState = urlParamsToDashboardState(searchParams);
					setSorting(dashboardState.sorting);
					let grouping = dashboardState.grouping;
					// Filter out branch grouping if there is only one branch
					if (branches.length <= 1) {
						grouping = grouping.filter((group) => group !== "branch");
					}
					setGrouping(grouping);
					setColumnOrder(dashboardState.columnOrder);
					setColumnVisibility(dashboardState.columnVisibility);
					setColumnFilters(dashboardState.columnFilterValues);
					setColumnFilterFns({ ...DEFAULT_FILTER_FNS, ...dashboardState.columnFilterFunctions });

					// Set selectedView to null but still load jobs since we have valid URL params
					setSelectedView(null);
					if (company) {
						const branchNames = getBranchesFromFilters(dashboardState.columnFilterValues);
						const regionNames = getRegionsFromFilters(dashboardState.columnFilterValues);
						const filter = buildJobFilter(company, branchNames, regionNames);
						const jobs = await API.Data.Jobs.get.byFilter(filter);
						setMultiSelectOptions(jobs, branches, regions);
						setJobs(jobs);
					}
				}
			} else {
				// No URL params, no default view selection
				setSelectedView(null);
				setJobs([]);
			}
		} catch {
			// handle error
		} finally {
			setIsLoading(false);
			setIsInitializing(false);
			setMobileMenuElement(null);
		}
	}

	async function onSavedViewChanged(event: any, value: any) {
		event.stopPropagation();
		if (!value) {
			setSelectedView(value);
			setJobs([]);
			return;
		}

		try {
			setIsLoading(true);

			const urlParams = new URLSearchParams(value.url);
			urlParams.sort();
			const dashboardState = urlParamsToDashboardState(urlParams);
			setSorting(dashboardState.sorting);
			let grouping = dashboardState.grouping;
			// This is special logic to filter out the branch grouping if there is only one branch
			if (branches.length <= 1) {
				grouping = grouping.filter((group) => group !== "branch");
			}
			setGrouping(grouping);
			setColumnOrder(dashboardState.columnOrder);
			setColumnVisibility(dashboardState.columnVisibility);
			setColumnFilters(dashboardState.columnFilterValues);
			setColumnFilterFns({ ...DEFAULT_FILTER_FNS, ...dashboardState.columnFilterFunctions });

			setSearchParams(urlParams);
			setSelectedView(value);

			// Load jobs when a view is selected
			if (selectedCompany) {
				const branchNames = getBranchesFromFilters(dashboardState.columnFilterValues);
				const regionNames = getRegionsFromFilters(dashboardState.columnFilterValues);
				const filter = buildJobFilter(selectedCompany, branchNames, regionNames);
				const jobs = await API.Data.Jobs.get.byFilter(filter);
				setMultiSelectOptions(jobs, branches, regions);
				setJobs(jobs);
			}
		} catch (error) {
			Oops.handleError(error, "JobsDashboardTable.onSavedViewChanged", userContext?.email);
		} finally {
			setIsLoading(false);
			setMobileMenuElement(null);
		}
	}

	async function refreshJobs() {
		await loadJobs();
	}
	async function loadJobs() {
		try {
			if (!selectedCompany || (!selectedView && !searchParams.toString())) {
				return;
			}
			setIsLoading(true);
			const branchNames = getBranchesFromFilters(columnFilters);
			const regionNames = getRegionsFromFilters(columnFilters);
			const filter = buildJobFilter(selectedCompany, branchNames, regionNames);
			const jobs = await API.Data.Jobs.get.byFilter(filter);
			setMultiSelectOptions(jobs, branches, regions);
			setJobs(jobs);
		} catch (error) {
			Oops.handleError(error, "JobsDashboardTable.loadJobs", userContext?.email);
		} finally {
			setIsLoading(false);
			setMobileMenuElement(null);
		}
	}

	async function updateDisplayedJobs() {
		try {
			const selectedJobs = Object.keys(rowSelection)
				.filter((key) => rowSelection[key])
				.map((key) => {
					const index = parseInt(key);
					return !isNaN(index) ? jobs[index] : undefined;
				})
				.filter(Boolean) as Job[];

			// open the drawer if rows are selected
			props.setDrawerOpenState(selectedJobs.length > 0);

			// Fetch geodata for each job and populate job.files
			const jobsWithGeodata = await Promise.all(
				selectedJobs.map(async (job) => {
					const [boundary, points, zones] = await Promise.all([
						GeoJSON.getBoundary(job),
						GeoJSON.getPoints(job),
						GeoJSON.getZones(job),
					]);

					return {
						...job,
						files: {
							...job.files,
							boundaryGeoJSON: boundary ?? undefined,
							pointsGeoJSON: points ?? undefined,
							zonesGeoJSON: zones ?? undefined,
						},
					} as Job;
				}),
			);

			props.setDisplayedJobs(jobsWithGeodata);
			setSelectionDisplayedOnMap(rowSelection);

			// Count how many have each type of geodata
			const boundaryCount = jobsWithGeodata.filter((j) => j.files.boundaryGeoJSON).length;
			const pointCount = jobsWithGeodata.filter((j) => j.files.pointsGeoJSON).length;
			const zoneCount = jobsWithGeodata.filter((j) => j.files.zonesGeoJSON).length;
			return [boundaryCount, pointCount, zoneCount] as const;
		} catch (error) {
			Oops.handleError(error, "JobsDashboardTable.updateDisplayedJobs", userContext?.email);
			return [0, 0, 0] as const;
		}
	}

	async function showOnMap() {
		const loadingBoundariesToast = toast.loading("Updating map...");
		const [boundaryCount, pointCount, zoneCount] = await updateDisplayedJobs();
		toast.dismiss(loadingBoundariesToast);
		toast.info(`Displaying ${boundaryCount} boundaries, ${pointCount} point groups and ${zoneCount} zones`);
		setMobileMenuElement(null);
	}

	function getMarkReadyText(unreadySelectedJobs: Job[]): React.ReactNode {
		switch (unreadySelectedJobs.length) {
			case 0:
				return "Mark Jobs Ready";
			case 1:
				return "Mark 1 Job Ready";
			default:
				return `Mark ${unreadySelectedJobs.length} Jobs Ready`;
		}
	}

	// need to also ensure that individual files aren't zipped
	function downloadFiles(
		jobIds: string[],
		files: IAirtableAttachment[],
		columnName: string,
		rowName: string,
		mergeCSV: boolean = false,
	) {
		return async () => {
			const toastDownloading = toast.loading("Downloading files...");

			try {
				await FileDownloader.downloadAttachments(files, getFileName(), files.length > 1, mergeCSV);
				Sentry.logger.info(`Downloaded ${columnName} file(s)`, {
					airtable_record_id: jobIds.join(","),
					username: userContext?.email,
				});
			} catch (error) {
				Oops.handleError(error, "JobsDashboardTable.downloadFiles", userContext?.email);
			} finally {
				toast.dismiss(toastDownloading);
			}

			function getFileName() {
				let fileName = "";

				if (files.length > 1) {
					fileName = rowName ? `${rowName} - ${columnName}` : "";
				}

				return fileName;
			}
		};
	}

	// PROOF OF CONCEPT
	// this function is pretty rudementary, and will need improvement before it can go live.
	// also, it doesn't yet work properly with `onColumnFilterFnsChange: setColumnFilterFns,`
	const booleanFilterFn: MRT_FilterFn<any> = (row, columnId, filterValue) => {
		const columnValue = row.getValue<string>(columnId).toLowerCase();
		const queryElements = filterValue.match(/\w+/g);
		const terms = queryElements
			?.filter((element: string) => element !== "AND" && element !== "OR")
			.map((element: string) => element.toLowerCase());
		const operators = filterValue.match(/(AND|OR)/g); // Extract operators

		if (!terms || terms.length === 0) {
			return true; // No filter applied
		}

		const evaluateAnds = (terms: string[], columnValue: string) => {
			return terms.reduce((acc, term) => acc && columnValue.includes(term), true);
		};

		let result = false;
		let currentTerms: string[] = [];
		let currentOperator = "OR";

		for (let i = 0; i < terms.length; i++) {
			currentTerms.push(terms[i]);
			const nextOperator = operators?.[i] || "OR";

			if (nextOperator === "OR" || i === terms.length - 1) {
				const andResult = evaluateAnds(currentTerms, columnValue);
				if (currentOperator === "AND") {
					result = result && andResult;
				} else {
					result = result || andResult;
				}
				currentTerms = [];
				currentOperator = nextOperator;
			}
		}

		return result || false;
	};

	function getShowMapText(): React.ReactNode {
		return selectionDisplayedOnMap !== rowSelection && props.drawerOpen ? "Update Map" : "Show on Map";
	}

	async function exportCSV() {
		try {
			const toastLoading = toast.loading("Generating CSV...");
			const rows = table.getFilteredRowModel().rows;
			const excludedColumns = ["Files", "Expand", "Select", "Map Issue"];
			const columns = table.getAllColumns().filter((col) => !excludedColumns.includes(col.columnDef.header));

			const csv = generateCSV(columns, rows);

			const blob = new Blob([csv], { type: "text/csv" });
			const filename = `rogo_jobs_export_${new Date().toISOString().split("T")[0]}.csv`;
			FileDownloader.downloadBlob(blob, filename);

			toast.dismiss(toastLoading);
			toast.success(`Exported ${rows.length} rows to CSV`);

			Sentry.logger.info("CSV export from Jobs Dashboard", {
				username: userContext?.email,
				airtable_record_count: jobs.length,
				airtable_record_id: jobs.map((job) => job.id).join(","),
			});
		} catch (error) {
			Oops.handleError(error, "JobsDashboardTable.exportCSV", userContext?.email);
		} finally {
			setMobileMenuElement(null);
		}

		function generateCSV(columns: MRT_Column<Job, unknown>[], rows: Row<Job>[]) {
			const headers = columns.map((col) => col.columnDef.header?.toString() || col.id);
			const csvRows = [headers.join(",")];
			for (const row of rows) {
				const rowData = [];

				for (const column of columns) {
					const value = row.getValue(column.id);

					if (value === null || value === undefined) {
						rowData.push("");
					} else if (typeof value === "boolean") {
						rowData.push(value ? "true" : "false");
					} else if (value instanceof Date) {
						rowData.push(value.toISOString().split("T")[0]); // Use YYYY-MM-DD format
					} else if (Array.isArray(value)) {
						const escapedValue = value.join(", ").replace(/"/g, '""'); // Escape any commas in array values
						rowData.push(`"${escapedValue}"`);
					} else {
						const stringValue = String(value);
						if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
							rowData.push(`"${stringValue.replace(/"/g, '""')}"`);
						} else {
							rowData.push(stringValue);
						}
					}
				}

				csvRows.push(rowData.join(","));
			}
			const csvContent = csvRows.join("\n");
			return csvContent;
		}
	}

	function deleteView() {
		if (selectedView) {
			const newViews: ViewDefinition[] = [...views];
			const index = newViews.indexOf(selectedView);
			newViews.splice(index, 1);
			setViews(newViews);
			setSelectedView(null);

			updateViews(selectedView, newViews);
		}
		setMobileMenuElement(null);
	}

	async function copyLinkToClipboard() {
		try {
			// Construct the complete URL with all current parameters
			const urlParams = dashboardStateToURLParams({
				sorting,
				grouping,
				columnOrder,
				columnVisibility,
				columnFilterFunctions: columnFilterFns,
				columnFilterValues: columnFilters,
			});

			// Include company parameter when multiple companies exist
			if (companyOptions.length > 1 && selectedCompany) {
				urlParams.set("company", selectedCompany.id);
			}

			// Construct the full URL
			const baseUrl = window.location.origin + window.location.pathname + window.location.hash.split("?")[0];
			const fullUrl = `${baseUrl}?${urlParams.toString()}`;

			await navigator.clipboard.writeText(fullUrl);
			toast.success("Link copied to clipboard");
		} catch (error) {
			Oops.handleError(error, "JobsDashboardTable.copyLinkToClipboard", userContext?.email);
		} finally {
			setMobileMenuElement(null);
		}
	}

	async function saveCurrentView() {
		// TODO prompt for name
		const result = await new Promise<[string, ViewGroup] | undefined>((resolve) => {
			setInputDialogState({
				open: true,
				title: "Save current view",
				content: "Please enter a name for this view",
				submitText: "Save",
				submit: (result) => {
					resolve(result);
					setInputDialogState({ ...inputDialogState, open: false });
				},
				viewName: selectedView?.name ?? "",
			});
		});

		if (!result) {
			setMobileMenuElement(null);
			return;
		}

		const [name, type] = result;

		const newViews: ViewDefinition[] = [...views];

		const newView: ViewDefinition = {
			name,
			group: type,
			url: searchParams.toString(),
			lastUpdated: new Date().toISOString(),
		};
		if (selectedView && name === selectedView.name && type === selectedView.group) {
			newViews[newViews.indexOf(selectedView)] = newView;
		} else {
			// look for a view that has the same group type as our new value
			for (const view of newViews) {
				if (view.group === newView.group || view.group === ViewGroup.Default) {
					// insert the view right before the first view with the same group
					newViews.splice(newViews.indexOf(view), 0, newView);
					break;
					// because we always sort the Default group to the
					// end of the list, we can break here
				}
			}
		}
		setViews(newViews);
		setSelectedView(newView);

		updateViews(newView, newViews);
		setMobileMenuElement(null);
	}

	function updateViews(newView: ViewDefinition, newViews: ViewDefinition[]) {
		if (newView.group === ViewGroup.User) {
			const userViews = newViews.filter((view) => view.group === ViewGroup.User);
			if (userContext) {
				API.Data.Contacts.update.portalSettings(userContext.id, { views: userViews });
			}
		} else if (newView.group === ViewGroup.Company) {
			const companyViews = newViews.filter((view) => view.group === ViewGroup.Company);
			if (selectedCompany) {
				if (!selectedCompany.portalSettings) {
					// @ts-ignore
					selectedCompany.portalSettings = { views: [] };
				}
				selectedCompany.portalSettings.views = companyViews;
				API.Data.Companies.update.portalSettings(selectedCompany.id, selectedCompany.portalSettings);
			}
		}
	}

	// #endregion

	// #region MENUS / TOOLBAR
	function toolbar(table: any) {
		const noJobs = jobs.length === 0;
		const noViewSelected = !selectedView && !searchParams.toString();
		const viewIsReadonly = !!selectedView && selectedView.group === ViewGroup.Default;

		// we will actually always allow the save button when we have a default view selected
		// that way we will allow the user to "copy" the view by saving it under their own name
		const canSaveViewState = !viewIsReadonly && !selectedViewDirty;
		const cantSaveViewState = !canSaveViewState;
		const saveViewDisabled = noViewSelected || (noJobs && cantSaveViewState);
		const deleteViewDisabled = viewIsReadonly || noViewSelected;

		return (
			<Box
				sx={(theme) => ({
					backgroundColor: lighten(theme.palette.background.default, 0.05),
					display: "flex",
					pl: "8px",
				})}
			>
				<div id="MRT-sub-components" className="horizontal-button-group">
					<MRT_GlobalFilterTextField table={table} />
					<MRT_ToggleFiltersButton table={table} />
					<MRT_ShowHideColumnsButton table={table} />
					<MRT_ToggleDensePaddingButton table={table} />
					<MRT_ToggleFullScreenButton table={table} />
				</div>

				<div id="right-controls">
					{/* only show controls if NOT expanded */}
					{!props.drawerOpen && (
						<>
							<div id="company-view-dropdowns" className="hide-on-mobile-screen">
								{companyOptions.length > 1 && (
									<CustomAutocomplete
										optionLabelField="name"
										loading={false}
										options={companyOptions}
										selectedOption={selectedCompany}
										onChange={onCompanyChanged}
										className="dashboard-dropdown"
										label="Company"
										// tooltip="Select a company to view their jobs and views. This is only for ROGO employees"
										placement="right"
										size="small"
									/>
								)}
								<Autocomplete
									className="dashboard-dropdown"
									options={views}
									value={selectedView}
									disabled={false}
									groupBy={(option) => (option.group === "Default" ? "System Views" : option.group.toString())}
									getOptionKey={(option) => `${option.group}-${option.name}`}
									getOptionLabel={(option) => option.name}
									onChange={onSavedViewChanged}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Saved View"
											placeholder="Select a view to load jobs"
											variant="outlined"
											size="small"
										/>
									)}
								/>
							</div>
							<div id="view-button" className="horizontal-button-group hide-on-small-screen">
								<Tooltip title="Refresh jobs">
									<span>
										<IconButton
											id="refresh-jobs-button"
											onClick={refreshJobs}
											disabled={noViewSelected}
											className="icon-button"
										>
											<RefreshOutlined fontSize="medium" style={{ color: noViewSelected ? "grey" : "blue" }} />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip title="Save current view">
									<span>
										<IconButton
											id="save-view-button"
											onClick={saveCurrentView}
											className="icon-button"
											disabled={saveViewDisabled}
										>
											<Save fontSize="medium" style={{ color: saveViewDisabled ? "grey" : "green" }} />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip title="Copy link to clipboard">
									<span>
										<IconButton
											id="copy-link-button"
											onClick={copyLinkToClipboard}
											className="icon-button"
											disabled={noViewSelected}
										>
											<ContentCopy fontSize="medium" style={{ color: noViewSelected ? "grey" : "blue" }} />
										</IconButton>
									</span>
								</Tooltip>
								<Tooltip title="Delete view">
									<span>
										<IconButton
											id="delete-view-button"
											onClick={deleteView}
											className="icon-button"
											disabled={deleteViewDisabled}
										>
											<Delete fontSize="medium" style={{ color: deleteViewDisabled ? "grey" : "red" }} />
										</IconButton>
									</span>
								</Tooltip>
							</div>
						</>
					)}
					<div id="toolbar-menu" className="horizontal-button-group">
						<div>
							<IconButton
								onClick={(event) => {
									setMobileMenuElement(event.currentTarget);
								}}
								className="icon-button"
								id="toolbar-menu-button"
							>
								<MoreHoriz />
							</IconButton>
						</div>
					</div>
					<div id="big-blue-buttons" className="horizontal-button-group">
						<div id="mark-ready-buttons" className="multi-screen-size-button-group hide-on-small-screen">
							<Button
								id="mark-ready-button"
								className="text-button big-screen-button"
								onClick={() => {
									setMarkJobsReadyVisible(true);
								}}
								color="primary"
								variant="contained"
							>
								{getMarkReadyText(unreadySelectedJobs)}
							</Button>
							<Tooltip title={getMarkReadyText(unreadySelectedJobs)}>
								<span>
									<IconButton
										className="icon-button medium-screen-button"
										onClick={() => {
											setMarkJobsReadyVisible(true);
										}}
									>
										<Check fontSize="medium" />
									</IconButton>
								</span>
							</Tooltip>
						</div>
						<div id="show-on-map-buttons" className="multi-screen-size-button-group">
							<Button
								id="show-on-map-button"
								onClick={showOnMap}
								className="text-button big-screen-button"
								color="primary"
								variant="contained"
								disabled={
									Object.keys(selectionDisplayedOnMap).length === Object.keys(rowSelection).length &&
									Object.keys(rowSelection).every((key) => !!selectionDisplayedOnMap[key])
								}
							>
								{getShowMapText()}
							</Button>
							<Tooltip title={getShowMapText()}>
								<span>
									<IconButton className="icon-button medium-screen-button" onClick={showOnMap}>
										<Map fontSize="medium" />
									</IconButton>
								</span>
							</Tooltip>
							{props.drawerOpen ? (
								<IconButton
									onClick={() => {
										props.setDrawerOpenState(!props.drawerOpen);
									}}
									className="icon-button"
									id="right-drawer-button"
									disabled={noViewSelected}
								>
									<KeyboardDoubleArrowRight fontSize="large" style={{ color: noViewSelected ? "grey" : undefined }} />
								</IconButton>
							) : (
								<></>
							)}
						</div>
					</div>
				</div>
			</Box>
		);
	}

	interface FileMenuItem {
		label: string;
		property: keyof Job["attachments"];
	}

	const fileMenuList: FileMenuItem[] = [
		{ label: "Boundary", property: "boundarySHP" },
		{ label: "Points", property: "pointsSHP" },
		{ label: "Sample Zones", property: "zonesSHP" },
		{ label: "Executed Points", property: "executedPointsSHP" },
		{ label: "Executed Boundary", property: "executedBoundarySHP" },
		{ label: "Executed Points + Boundary", property: "executedPointsBoundarySHP" },
		{ label: "Lab Results", property: "labResults" },
		{ label: "Lab Checkin Sheet", property: "labPDF" },
		{ label: "Sampling CSV", property: "labCSV" },
	];

	function downloadMenu() {
		const downloadMenuItems = [];

		downloadMenuItems.push(
			<MenuItem key="header">
				<strong>Files for {getItemName()}</strong>
			</MenuItem>,
		);

		downloadMenuItems.push(
			<MenuItem
				key="all-files"
				onClick={downloadFiles(
					jobs.map((job) => job.id),
					getAllFiles(),
					"All Files",
					getItemName(),
				)}
			>
				<Download /> All Files
			</MenuItem>,
		);

		fileMenuList.forEach((fileMenuItem) => {
			addDownloadButton(fileMenuItem);
		});
		if (!actionMenuRow || actionMenuRow?.getIsGrouped()) {
			const csvs = getFiles({ label: "Sampling CSV", property: "labCSV" });
			downloadMenuItems.push(
				<MenuItem
					key="sampling-csv-merged"
					onClick={downloadFiles(
						selectedJobs.filter((j) => !!j).map((job) => job.id),
						csvs,
						"Sampling CSV (Merged)",
						getItemName(),
						true,
					)}
				>
					<Download /> Sampling CSV (Merged)
				</MenuItem>,
			);
		}

		if (user?.isDev && getJobID() != "") {
			downloadMenuItems.push(
				<MenuItem key="job-id" onClick={copyJobID}>
					<strong>{getJobID()}</strong>
				</MenuItem>,
			);
		}

		return (
			<Menu anchorEl={actionMenuElement} open={Boolean(actionMenuElement)} onClose={() => setActionMenuElement(null)}>
				{downloadMenuItems}
			</Menu>
		);

		function copyJobID() {
			navigator.clipboard
				.writeText(getJobID())
				.then(() => {
					toast.success("Job ID copied to clipboard");
				})
				.catch(() => {
					toast.error("Failed to copy Job ID to clipboard");
				});
			return;
		}

		function getJobID() {
			let jobid = "";

			if (!actionMenuRow) {
				if (selectedJobs.length === 0) {
					jobid = "";
				} else {
					jobid = "";
				}
			} else if (actionMenuRow.getIsGrouped()) {
				jobid = "";
			} else {
				const job = actionMenuRow?.original;
				jobid = job.id;
			}

			return jobid;
		}

		function getAllFiles() {
			const allFiles: IAirtableAttachment[] = [];

			fileMenuList.forEach((fileMenuItem) => {
				const files = getFiles(fileMenuItem);
				allFiles.push(...files);
			});

			return allFiles;
		}

		function getItemName() {
			let itemName = "";

			if (!actionMenuRow) {
				if (selectedJobs.length === 0) {
					itemName = "All Jobs";
				} else {
					itemName = "Selected Jobs";
				}
			} else if (actionMenuRow.getIsGrouped()) {
				itemName = actionMenuRow.groupingValue as string;
			} else {
				const job = actionMenuRow?.original;
				itemName = job.fieldName;
			}

			return itemName;
		}

		function addDownloadButton(fieldNames: FileMenuItem) {
			const rowName = getRowName();
			const files = getFiles(fieldNames);

			if (files?.length > 0) {
				downloadMenuItems.push(downloadButton(fieldNames.label, files, rowName));
			} else {
				downloadMenuItems.push(
					<MenuItem key={fieldNames.property} disabled>
						<Download /> {fieldNames.label}
					</MenuItem>,
				);
			}

			function getRowName() {
				let rowName = "";
				if (!actionMenuRow) {
					if (selectedJobs.length === 0) {
						rowName = "All Jobs";
					} else {
						rowName = "Selected Jobs";
					}
				} else if (actionMenuRow.getIsGrouped()) {
					rowName = actionMenuRow.groupingValue as string;
				} else {
					rowName = actionMenuRow.original.fieldName;
				}
				return rowName;
			}
		}

		function getFiles(fileMenuItem: FileMenuItem) {
			const files: IAirtableAttachment[] = [];

			if (!actionMenuRow) {
				// All files
				if (selectedJobs.length === 0) {
					for (let i = 0; i < (jobs.length ?? 0); i++) {
						getFilesFromJob(jobs[i]);
					}
					// Selected files
				} else {
					for (let i = 0; i < (selectedJobs.length ?? 0); i++) {
						getFilesFromJob(selectedJobs[i]!);
					}
				}
				// Grouped files
			} else if (actionMenuRow.getIsGrouped()) {
				const getFilesRecursive = (subRows: MRT_Row<Job>[]) => {
					subRows.forEach((subRow) => {
						if (subRow.subRows?.length) {
							getFilesRecursive(subRow.subRows);
						} else {
							getFilesFromJob(subRow.original);
						}
					});
				};

				getFilesRecursive(actionMenuRow?.subRows ?? []);
				// Single file
			} else {
				getFilesFromJob(actionMenuRow?.original);
			}

			return files;

			function getFilesFromJob(job: Job | undefined) {
				if (job) {
					const file = job.attachments[fileMenuItem.property];
					if (file) files.push(...file);
				}
			}
		}

		function downloadButton(label: string, files: IAirtableAttachment[], groupName: string) {
			return (
				<MenuItem
					key={label.toLowerCase().replace(/\s+/g, "-")}
					onClick={downloadFiles([actionMenuRow?.original?.id ?? ""], files, label, groupName)}
				>
					<Download /> {label}
				</MenuItem>
			);
		}
	}

	function mobileMenu() {
		return (
			<Menu anchorEl={mobileMenuElement} open={Boolean(mobileMenuElement)} onClose={() => setMobileMenuElement(null)}>
				{companyOptions.length > 1 && (
					<CustomAutocomplete
						id="company-dropdown"
						optionLabelField="name"
						loading={false}
						options={companyOptions}
						selectedOption={selectedCompany}
						onChange={onCompanyChanged}
						className="dashboard-dropdown show-on-mobile-screen"
						label="Company"
						// tooltip="Select a company to view their jobs and views. This is only for ROGO employees"
						placement="right"
						size="small"
					/>
				)}
				<Autocomplete
					id="view-dropdown"
					className="dashboard-dropdown show-on-mobile-screen"
					options={views}
					value={selectedView}
					groupBy={(option) => (option.group === "Default" ? "System Views" : option.group.toString())}
					getOptionKey={(option) => option.group + option.name}
					getOptionLabel={(option) => option.name}
					onChange={onSavedViewChanged}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Saved View"
							placeholder="Select a view to load jobs"
							variant="outlined"
							size="small"
						/>
					)}
				/>
				<MenuItem
					onClick={refreshJobs}
					className="show-on-small-screen"
					disabled={!selectedView && !searchParams.toString()}
				>
					<RefreshOutlined />
					<div className="right-menu-text">Refresh Jobs</div>
				</MenuItem>
				<MenuItem
					onClick={saveCurrentView}
					className="show-on-small-screen"
					disabled={!selectedView && !searchParams.toString()}
				>
					<Save />
					<div className="right-menu-text">Save Current View</div>
				</MenuItem>
				<MenuItem
					onClick={copyLinkToClipboard}
					className="show-on-small-screen"
					disabled={!selectedView && !searchParams.toString()}
				>
					<ContentCopy />
					<div className="right-menu-text">Copy Link to Clipboard</div>
				</MenuItem>
				<MenuItem onClick={deleteView} className="show-on-small-screen" disabled={!selectedView}>
					<Delete />
					<div className="right-menu-text">Delete View</div>
				</MenuItem>
				<MenuItem
					onClick={() => {
						setMarkJobsReadyVisible(true);
					}}
					className="show-on-small-screen"
					disabled={!selectedView && !searchParams.toString()}
				>
					<Check />
					<div className="right-menu-text">Mark Jobs Ready</div>
				</MenuItem>
				<MenuItem
					onClick={showOnMap}
					className="show-on-small-screen"
					disabled={!selectedView && !searchParams.toString()}
				>
					<Map />
					<div className="right-menu-text">Show on Map</div>
				</MenuItem>
				<MenuItem onClick={exportCSV} disabled={!selectedView && !searchParams.toString()}>
					<FileDownload />
					<div className="right-menu-text">Export as CSV</div>
				</MenuItem>
				{/* <MenuItem onClick={loadAllJobs} disabled={!selectedView}>
        <HourglassBottom /><div className="right-menu-text">Load All Jobs</div>
      </MenuItem> */}
			</Menu>
		);
	}
	// #endregion

	// #region TABLE
	const columns = useMemo<MRT_ColumnDef<Job>[]>(
		() => [
			textColumn("Grower", { accessorKeyOrFn: "growerName", size: 200 }),
			textColumn("Field", { accessorKeyOrFn: "fieldName" }),
			textColumn("Farm", { accessorKeyOrFn: "farmName" }),
			multiSelectColumn("Job Status", { accessorKeyOrFn: "status", filterSelectOptions: jobStatusList }),
			{
				id: "boundary acres",
				accessorFn: (row) => row.boundaryAcres ?? 0,
				header: "Boundary Acres",
				size: 100,
				filterVariant: "range",
				filterFn: "betweenInclusive",
				aggregationFn: "sum",
				Cell: ({ cell }) => (
					<Box
						component="span"
						sx={{ borderRadius: "0.25rem", maxWidth: "9ch", p: "0.25rem", textAlign: "right", display: "block" }}
					>
						{cell.getValue<number>() ? Math.round(cell.getValue<number>()) : "-"}
					</Box>
				),
				AggregatedCell: ({ row }) => {
					const leafRows = row.getLeafRows();
					const values = leafRows.map((r) => r.original.boundaryAcres).filter((v) => v != null && !isNaN(v));
					const sum = values.reduce((acc, v) => acc + v, 0);
					const average = values.length > 0 ? sum / values.length : 0;
					const displayValue = acresAggregation === "total" ? sum : average;
					const suffix = acresAggregation === "total" ? " total" : " average";

					return (
						<PortalTooltip title={`Click to show ${acresAggregation === "total" ? "average" : "total"}`}>
							<Box
								component="span"
								onClick={() => setAcresAggregation((prev) => (prev === "total" ? "average" : "total"))}
								sx={{
									cursor: "pointer",
									fontWeight: "bold",
									borderRadius: "0.25rem",
									color: "#000",
									maxWidth: "12ch",
									p: "0.25rem",
									textAlign: "right",
									display: "block",
								}}
							>
								{Math.round(displayValue)}
								{suffix}
							</Box>
						</PortalTooltip>
					);
				},
			} as MRT_ColumnDef<Job>,
			multiSelectColumn("Branch", { accessorKeyOrFn: getBranchField, filterSelectOptions: branchList }),
			multiSelectColumn("Region", { accessorKeyOrFn: getRegionField, filterSelectOptions: regionList }),
			dateColumn("Field Ready Date", { accessorKeyOrFn: "dateFieldReady", isRangeFilter: true }),
			dateColumn("Sample Date", { accessorKeyOrFn: "dateSampled", isRangeFilter: true }),
			dateColumn("Creation Date", { accessorKeyOrFn: "dateCreated", isRangeFilter: true }),
			dateColumn("Drop/Ship Date", { accessorKeyOrFn: "dateShipped", isRangeFilter: true }),
			multiSelectColumn("Test Package", { accessorKeyOrFn: "testPackage", filterSelectOptions: testPackageList }),
			checkboxColumn("Boundary Change?", { accessorKeyOrFn: "isBoundaryChanged" }),
			checkboxColumn("Boundary Approved?", { accessorKeyOrFn: "isBoundaryApproved" }),
			checkboxColumn("Points Change?", { accessorKeyOrFn: "isPointsChanged" }),
			multiSelectColumn("Submitter", { accessorKeyOrFn: "submitterName", filterSelectOptions: submitterList }),
			multiSelectColumn("Season", { accessorKeyOrFn: "season", filterSelectOptions: seasonList }),
			checkboxColumn("Approved Billing?", { accessorKeyOrFn: "isBillingApproved" }),
			checkboxMultiselectColumn("Job Flags", "Map Issue", "jobFlags"),
			dateColumn("Lab Sent Results", { accessorKeyOrFn: "dateLabResultsSent", isRangeFilter: true }),
			textColumn("Event ID", { accessorKeyOrFn: "eventId" }),
			textColumn("Lab Submittal ID", { accessorKeyOrFn: "labSubmittalId" }),
			numericColumn("Depth", { accessorKeyOrFn: "depth", aggregationFn: "sum", slider: false, sliderStepSize: 1 }),
			textColumn("Sample Changes", { accessorKeyOrFn: "sampleChanges" }),
			textColumn("Lab Name", { accessorKeyOrFn: "labName" }),
			checkboxColumn("Test Job?", { accessorKeyOrFn: "isTestJob" }),
			numericColumn("Samples", { accessorKeyOrFn: "numberOfSamples", aggregationFn: "sum", slider: false }),
		],
		[
			branches,
			jobStatusList,
			testPackageList,
			submitterList,
			seasonList,
			branchList,
			regionList,
			regions,
			acresAggregation,
		],
	);

	const table = useMaterialReactTable({
		columns,
		data: jobs, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
		enableColumnFilterModes: true,
		enableColumnFilters: true,
		enableColumnOrdering: true,
		enableColumnDragging: true,
		enableColumnPinning: true,
		enableGrouping: true,
		enableHiding: true,
		enableFacetedValues: true,
		enableStickyHeader: true,
		enableStickyFooter: true,
		enableTableFooter: true,
		enableMultiSort: true,
		enableGlobalFilterModes: true,
		enableGlobalFilterRankedResults: true,
		enableRowSelection: true,
		enableSubRowSelection: true,
		enableRowActions: true,
		positionActionsColumn: "last",
		columnFilterDisplayMode: "subheader",
		muiTableContainerProps: { sx: { height: tableHeight } },
		muiTablePaperProps: { sx: { height: "100vh - 30px)" } },
		maxMultiSortColCount: 3,
		initialState: {
			expanded: true,
			showGlobalFilter: true,
			density: "compact",
			pagination: {
				pageSize: 20,
				pageIndex: 0,
			},
			columnVisibility: {
				Grower: false,
				"Test Job?": false,
			},
			columnPinning: {
				left: ["mrt-row-select"],
				right: ["mrt-row-actions"],
			},
		},
		filterFns: {
			booleanFilter: booleanFilterFn,
		},
		selectAllMode: "all",
		state: {
			// URL based state
			columnVisibility,
			grouping,
			sorting,
			columnOrder,
			columnFilters: columnFilters,
			columnFilterFns: columnFilterFns,
			rowSelection,
			isLoading: isLoading || updatingBoundaries,
		},
		muiCircularProgressProps: {
			Component: <LoadingComponent />,
		},
		onGroupingChange: setGrouping,
		onRowSelectionChange: setRowSelection,
		onColumnVisibilityChange: setColumnVisibility,
		onColumnOrderChange: setColumnOrder,
		onColumnFilterFnsChange: setColumnFilterFns,
		onColumnFiltersChange: setColumnFilters,
		onSortingChange: setSorting,
		groupedColumnMode: false,
		displayColumnDefOptions: {
			"mrt-row-select": {
				size: 40,
				visibleInShowHideMenu: true,
			},
			"mrt-row-actions": {
				header: "Files",
				size: 40,
				visibleInShowHideMenu: true,
				Header: () => {
					return (
						<Box className="download-button">
							<IconButton
								color="primary"
								onClick={(event) => {
									setActionMenuElement(event?.currentTarget);
									setActionMenuRow(null);
								}}
							>
								<Download />
							</IconButton>
							<Box className="hide-on-small-screen">Files</Box>
						</Box>
					);
				},
				AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => {
					return (
						<Box className="download-button">
							<IconButton
								color="primary"
								onClick={(event) => {
									setActionMenuElement(event?.currentTarget);
									setActionMenuRow(row);
								}}
							>
								<Download />
							</IconButton>
							<Box className="hide-on-small-screen">Files</Box>
						</Box>
					);
				},
				Cell: ({ row }: { row: MRT_Row<Job> }) => {
					return (
						<Box className="download-button">
							<IconButton
								color="primary"
								onClick={(event) => {
									setActionMenuElement(event?.currentTarget);
									setActionMenuRow(row);
								}}
							>
								<Download />
							</IconButton>
							<Box className="hide-on-small-screen">Files</Box>
						</Box>
					);
				},
			},
			"mrt-row-expand": {
				Header: () => (
					<Stack direction="row" alignItems="center">
						<MRT_ExpandAllButton table={table} />
						<Box>Groups</Box>
					</Stack>
				),
				// @ts-ignore
				GroupedCell: ({ row }) => {
					const value = row.getValue(grouping[grouping.length - 1]);
					if (value instanceof Date) {
						return value.toLocaleDateString();
					}
					return value;
				},
				Cell: ({ row }: { row: MRT_Row<Job> }) => {
					const groupingId = grouping[Math.min(row.depth, grouping.length - 1)];
					const value = row.getValue(groupingId);

					let label: string | unknown = value;
					if (value instanceof Date) {
						label = value.toLocaleDateString();
					}
					return (
						<Stack direction="row" alignItems="center">
							<MRT_ExpandButton row={row} table={table} />
							<Box>{label?.toString() || "n/a"}</Box>
						</Stack>
					);
				},
				muiTableBodyCellProps: ({ row }) => ({
					sx: (theme) => ({
						color:
							row.depth === 0 ? theme.palette.primary.main : row.depth === 1 ? theme.palette.secondary.main : undefined,
					}),
				}),
				size: 200,
				visibleInShowHideMenu: true,
			},
		},
		paginationDisplayMode: "default",
		positionToolbarAlertBanner: "bottom",
		muiSearchTextFieldProps: {
			size: "small",
			variant: "outlined",
		},
		muiPaginationProps: {
			rowsPerPageOptions: [10, 15, 20, 30, 50],
			showRowsPerPage: !props.drawerOpen,
		},
		renderTopToolbar: ({ table }) => {
			return toolbar(table);
		},
		localization: {
			noRecordsToDisplay: "No jobs found. Please select a saved view above.",
		},
	});

	return (
		<>
			<MaterialReactTable table={table} />
			<ViewSaveDialog {...inputDialogState} />
			<MarkJobReadyPopover
				open={markJobsReadyVisible}
				jobs={jobs.filter((job) => !job.dateFieldReady)}
				initialSelectedJobs={markJobsReadyVisible ? unreadySelectedJobs : []}
				setLoading={setIsLoading}
				onSuccess={refreshJobs}
				setOpen={(open) => setMarkJobsReadyVisible(open)}
			/>
			{downloadMenu()}
			{mobileMenu()}
		</>
	);
	// #endregion
}
// #endregion
